/**
 * PushNotificationsCard — self-contained toggle to enable / disable
 * browser push notifications. Handles all edge cases:
 *   • unsupported browser → shows an info banner, no toggle
 *   • permission=granted   → shows "Enabled" + "Send test" + "Disable"
 *   • permission=default   → shows "Enable notifications" primary CTA
 *   • permission=denied    → shows guidance to unblock in browser settings
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, BellOff, Send, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  isPushSupported, getNotificationPermission, enablePush, disablePush, sendTestPush,
} from "@/lib/push";

export function PushNotificationsCard() {
  const [supported] = useState(isPushSupported());
  const [permission, setPermission] = useState(getNotificationPermission());
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supported) return undefined;
    let cancelled = false;
    navigator.serviceWorker.ready.then((reg) => reg.pushManager.getSubscription())
      .then((sub) => { if (!cancelled) setSubscribed(!!sub); })
      .catch(() => { /* no SW yet in dev */ });
    return () => { cancelled = true; };
  }, [supported]);

  const onEnable = async () => {
    setBusy(true);
    try {
      await enablePush();
      setPermission(getNotificationPermission());
      setSubscribed(true);
      toast.success("Push notifications enabled");
    } catch (e) {
      toast.error(e.message || "Could not enable notifications");
    } finally {
      setBusy(false);
    }
  };

  const onDisable = async () => {
    setBusy(true);
    try {
      await disablePush();
      setSubscribed(false);
      toast.success("Push notifications disabled");
    } catch (e) {
      toast.error(e.message || "Could not disable notifications");
    } finally {
      setBusy(false);
    }
  };

  const onTest = async () => {
    setBusy(true);
    try {
      const r = await sendTestPush();
      toast.success(`Test sent to ${r.delivered} device${r.delivered === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="push-notifications-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Bell size={16} /> Push notifications
        </CardTitle>
        <CardDescription className="text-xs">
          Get alerted the moment a trip is dispatched, or your bowser is nearby — even when the app is closed.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {!supported && (
          <div className="flex items-start gap-2 rounded-md border border-border/60 bg-muted/40 p-3 text-xs">
            <AlertTriangle size={14} className="mt-0.5 shrink-0 text-muted-foreground" />
            <span>Your browser doesn&apos;t support push notifications. Install the FuelExpress app from your browser menu, or use Chrome/Edge/Firefox on desktop or Android.</span>
          </div>
        )}
        {supported && permission === "denied" && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs">
            <AlertTriangle size={14} className="mt-0.5 shrink-0 text-destructive" />
            <span>Notifications are blocked. Click the lock/tune icon in your address bar, set <b>Notifications → Allow</b>, then reload.</span>
          </div>
        )}
        {supported && permission !== "denied" && (
          <div className="flex flex-wrap items-center gap-2">
            {!subscribed ? (
              <Button size="sm" onClick={onEnable} disabled={busy} data-testid="push-enable-btn">
                <Bell size={14} className="mr-1.5" />
                {busy ? "Enabling…" : "Enable notifications"}
              </Button>
            ) : (
              <>
                <Button size="sm" variant="outline" onClick={onTest} disabled={busy} data-testid="push-test-btn">
                  <Send size={14} className="mr-1.5" /> Send test
                </Button>
                <Button size="sm" variant="ghost" onClick={onDisable} disabled={busy} data-testid="push-disable-btn">
                  <BellOff size={14} className="mr-1.5" /> Disable
                </Button>
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-75"></span>
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500"></span>
                  </span>
                  Enabled
                </span>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
